import { useEffect } from "react";
import { useForm, useFormState, Controller, useWatch } from "react-hook-form";
import { Input } from "src/components/ui/input";
import { Switch } from "src/components/ui/switch";
import { Label } from "src/components/ui/label";
import { Button } from "src/components/ui/button";

interface SectionTitlesFormProps {
	defaultValues: SectionTitlesFormValues;
	onSubmit: (
		data: SectionTitlesFormValues,
		reset: (values: SectionTitlesFormValues) => void
	) => void;
	isSubmitting: boolean;
	onDirtyChange?: (dirty: boolean) => void;
}

const SectionTitlesForm = ({
	defaultValues,
	onSubmit,
	isSubmitting,
	onDirtyChange
}: SectionTitlesFormProps) => {
	const { control, handleSubmit, register, reset, trigger } =
		useForm<SectionTitlesFormValues>({
			defaultValues,
			mode: "onChange"
		});

	const { isDirty, errors } = useFormState({ control });
	const sections = useWatch({ control, name: "sections" });

	// Notify parent when dirty state changes
	useEffect(() => {
		onDirtyChange?.(isDirty);
	}, [isDirty, onDirtyChange]);

	// Re-trigger title validation when shown toggles change
	useEffect(() => {
		if (sections) {
			sections.forEach((_, index) => {
				trigger(`sections.${index}.title`);
			});
		}
	}, [(sections ?? []).map((s) => s.shown).join(","), trigger]);

	const preventEnterSubmit = (e: React.KeyboardEvent) => {
		const target = e.target as HTMLElement;
		if (e.key === "Enter" && target.tagName === "INPUT") e.preventDefault();
	};

	return (
		<form
			onSubmit={handleSubmit((data) => onSubmit(data, reset))}
			onKeyDown={preventEnterSubmit}
			className="flex flex-col gap-4"
		>
			{(sections ?? []).map((section, index) => {
				const fieldError = errors.sections?.[index]?.title;

				return (
					<div
						key={section.key}
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							fieldError ? "border-destructive/60" : "border-border"
						].join(" ")}
					>
						{fieldError && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-destructive text-destructive-foreground text-xs px-2 py-0.5 rounded-full font-medium"
								>
									{fieldError.message}
								</span>
							</div>
						)}
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground block mb-4">
								{section.label}
							</label>

							<div className="flex flex-col gap-4">
								<div>
									<Label
										htmlFor={`sections.${index}.title`}
										className="text-sm font-medium text-muted-foreground mb-1.5 block"
									>
										Title
									</Label>
									<Input
										id={`sections.${index}.title`}
										{...register(`sections.${index}.title`, {
											maxLength: {
												value: 100,
												message: "Title must be 100 characters or fewer"
											},
											validate: (value) => {
												const shown = sections?.[index]?.shown;
												if (shown && !value?.trim()) {
													return "Title is required when shown is enabled";
												}
												return true;
											}
										})}
										placeholder={`Enter ${section.label.toLowerCase()} title`}
									/>
								</div>

								<div className="flex items-center gap-3">
									<Controller
										control={control}
										name={`sections.${index}.shown`}
										render={({ field }) => (
											<Switch
												id={`sections.${index}.shown`}
												checked={field.value}
												onCheckedChange={field.onChange}
											/>
										)}
									/>
									<Label
										htmlFor={`sections.${index}.shown`}
										className="text-sm font-medium text-muted-foreground cursor-pointer"
									>
										Show title
									</Label>
								</div>
							</div>
						</div>
					</div>
				);
			})}

			<div className="flex justify-end pt-4">
				<Button type="submit" disabled={!isDirty || isSubmitting}>
					{isSubmitting ? (
						<span className="flex items-center gap-2">
							<span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
							Saving...
						</span>
					) : (
						"Save"
					)}
				</Button>
			</div>
		</form>
	);
};

export default SectionTitlesForm;
